import { useState, useMemo } from 'react'
import { Plus, Settings2, Play, Square } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useOrderbookStatus, useSaveOrderbookAlert, useDeleteOrderbookAlert, useStartOrderbook, useStopOrderbook } from '@/hooks/useAlerts'
import { useGlobalSettings, useUpdateOrderbookAlertsIgnore } from '@/hooks/useSettings'
import { useAuth } from '@/hooks/useAuth'
import { StatsCard, ExchangeBadge, GlobalIgnoreList } from '@/components/common'
import { OrderbookAlertEditSheet } from '@/components/alerts/OrderbookAlertEditSheet'
import type { OrderbookAlert } from '@/types'

export default function Orderbook() {
  const { data: status, isLoading } = useOrderbookStatus()
  const { data: settings } = useGlobalSettings()
  const saveAlert = useSaveOrderbookAlert()
  const deleteAlert = useDeleteOrderbookAlert()
  const updateIgnoreList = useUpdateOrderbookAlertsIgnore()
  const startOrderbook = useStartOrderbook()
  const stopOrderbook = useStopOrderbook()
  const { isAdmin } = useAuth()

  const [editingAlert, setEditingAlert] = useState<OrderbookAlert | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const alerts = status?.orderbook_alerts || []
  const isRunning = status?.running ?? true
  const globalIgnoreList = settings?.orderbook_alerts_ignore || []

  // Aggregate all per-alert ignore symbols into one list
  const allIgnoredSymbols = useMemo(() => {
    const symbols = new Set<string>()
    alerts.forEach(alert => {
      (alert.ignore || []).forEach(symbol => symbols.add(symbol))
    })
    return Array.from(symbols).sort()
  }, [alerts])

  const handleAddAlert = () => {
    setEditingAlert(null)
    setIsSheetOpen(true)
  }

  const handleEditAlert = (alert: OrderbookAlert) => {
    setEditingAlert(alert)
    setIsSheetOpen(true)
  }

  const handleSaveAlert = async (alertData: Partial<OrderbookAlert>) => {
    await saveAlert.mutateAsync(alertData)
  }

  const handleDeleteAlert = async (id: string) => {
    await deleteAlert.mutateAsync(id)
  }

  const handleUpdateIgnoreList = async (symbols: string[]) => {
    await updateIgnoreList.mutateAsync(symbols)
  }

  return (
    <div className="flex flex-col">
      <Header title="Orderbook" />

      <div className="flex-1 space-y-6 p-6">
        {/* Header with Add Alert and Global Ignore */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={isRunning ? 'success' : 'secondary'}>
              {isRunning ? 'Active' : 'Stopped'}
            </Badge>
            {isAdmin && (
              isRunning ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => stopOrderbook.mutate()}
                  disabled={stopOrderbook.isPending}
                >
                  <Square className="mr-2 h-3 w-3" />
                  {stopOrderbook.isPending ? 'Stopping...' : 'Stop'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => startOrderbook.mutate()}
                  disabled={startOrderbook.isPending}
                >
                  <Play className="mr-2 h-3 w-3" />
                  {startOrderbook.isPending ? 'Starting...' : 'Start'}
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <GlobalIgnoreList
              symbols={globalIgnoreList}
              alertType="orderbook"
              onUpdate={handleUpdateIgnoreList}
              isUpdating={updateIgnoreList.isPending}
              compact
            />
            <Button variant="outline" onClick={handleAddAlert}>
              <Plus className="mr-2 h-4 w-4" />
              Add Alert
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border-blue-500/30 bg-blue-500/10">
          <CardContent className="py-4">
            <p className="text-sm text-blue-300">
              <strong>What are Orderbook alerts?</strong> Monitors large orders in the
              orderbook across exchanges. Alerts when significant buy/sell walls appear
              or disappear.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Tracked Orders"
            value={status?.tracked_count || 0}
            description="Orders being monitored"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Notified"
            value={status?.notified_count || 0}
            description="Notifications sent"
          />
          <StatsCard
            title="Configured Alerts"
            value={alerts.length}
            description="Active alert configurations"
          />
        </div>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configured Alerts</CardTitle>
            <CardDescription>
              Orderbook monitoring configurations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No orderbook alerts configured. Click "Add Alert" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{alert.name || alert.id || `Alert ${idx + 1}`}</span>
                        <Badge variant={alert.enabled !== false ? 'success' : 'secondary'}>
                          {alert.enabled !== false ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(alert.exchanges || []).map((ex) => (
                          <ExchangeBadge key={ex} exchange={ex} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Min Order: ${(alert.min_usd_value ?? alert.min_order_usdt ?? 0).toLocaleString()} USDT
                      </p>
                      {/* Per-alert Ignore Symbols */}
                      {(alert.ignore && alert.ignore.length > 0) && (
                        <p className="text-xs text-muted-foreground">
                          Ignore: {alert.ignore.join(', ')}
                        </p>
                      )}
                      {/* Tags */}
                      {(alert.tag_expression || (alert.required_tags?.length || 0) > 0 || (alert.exclude_tags?.length || 0) > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {alert.tag_expression ? (
                            <Badge variant="outline" className="text-xs font-mono">
                              {alert.tag_expression}
                            </Badge>
                          ) : (
                            <>
                              {(alert.required_tags || []).map((tag) => (
                                <Badge key={tag} className="bg-green-500/20 text-green-500 text-xs">
                                  +{tag}
                                </Badge>
                              ))}
                              {(alert.exclude_tags || []).map((tag) => (
                                <Badge key={tag} variant="destructive" className="text-xs">
                                  -{tag}
                                </Badge>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditAlert(alert)}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Sheet */}
      <OrderbookAlertEditSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        alert={editingAlert}
        onSave={handleSaveAlert}
        onDelete={handleDeleteAlert}
      />
    </div>
  )
}
